import { useState, useCallback, useRef } from 'react';
import { ChatMessage, Session, StreamMessage, ChartData } from '../types';
import { createTicket, createSession, createEventSource } from '../utils/api';
import demoReports from '../data/demoReports';

interface UseDeepSearchReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  session: Session | null;
  error: string | null;
  submitQuestion: (question: string) => Promise<void>;
  loadDemoReport: (demoId: string) => void;
  cancelSearch: () => void;
  clearChat: () => void;
}

export function useDeepSearch(): UseDeepSearchReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<(() => void) | null>(null);

  const submitQuestion = useCallback(async (question: string) => {
    if (!question.trim()) return;

    // 清理之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current();
    }

    setIsLoading(true);
    setError(null);

    // 添加用户消息
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: question,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      // 1. 创建 ticket
      const ticket = await createTicket();

      // 2. 创建 session
      const sessionId = await createSession(question, ticket);

      // 3. 连接 SSE 获取流式响应
      // 一次请求中的 reasoning 消息和 正式的报告消息 其实 消息id是相同的，在服务器端 message id也是一样的。
      // 但 服务器端返回的event数据中，type 字段可能不同，reasoning消息的type为reasoning，正式的报告消息 type字段为 text.
      // 前后按照 message.id 为准来区分单次用户的报告请求。
      let assistantMessageId = '';
      let currentContent = '';
      let currentReasoning = '';
      let currentCharts: ChartData[] = [];

      const eventSource = createEventSource(
        sessionId,
        ticket,
        (message: StreamMessage) => {
          switch (message.type) {
            case 'begin':
              if (typeof message.content === 'object' && message.content !== null && 'sessionId' in message.content) {
                setSession(message.content as Session);
              }
              break;

            case 'text':
              if (message.role === 'assistant') {
                if (!assistantMessageId) {
                  assistantMessageId = message.id;
                  const assistantMessage: ChatMessage = {
                    id: message.id,
                    role: 'assistant',
                    content: typeof message.content === 'string' ? message.content : '',
                    reasoningContent: message.reasoningContent,
                    timestamp: message.timestamp,
                    isStreaming: true,
                    charts: currentCharts.length > 0 ? [...currentCharts] : undefined,
                  };
                  setMessages((prev) => [...prev, assistantMessage]);
                } else {
                  currentContent = typeof message.content === 'string' ? message.content : currentContent;

                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === assistantMessageId
                        ? {
                            ...msg,
                            content: currentContent,
                            charts: currentCharts.length > 0 ? [...currentCharts] : undefined,
                          }
                        : msg
                    )
                  );
                }
              }
              break;

            case 'reasoning':
              if (!assistantMessageId) {
                  assistantMessageId = message.id;
                  const assistantMessage: ChatMessage = {
                    id: message.id,
                    role: 'assistant',
                    content: '',
                    reasoningContent: message.reasoningContent,
                    timestamp: message.timestamp,
                    isStreaming: true,
                  };
                  setMessages((prev) => [...prev, assistantMessage]);
              } else {
                currentReasoning =  message.reasoningContent || '';
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessageId
                      ? { ...msg, reasoningContent: currentReasoning }
                      : msg
                  )
                );
              }
              break;

            case 'chart':
              // 处理图表消息
              if (message.content && typeof message.content === 'object' && 'code' in message.content) {
                const chartData = message.content as ChartData;
                currentCharts = [...currentCharts, chartData];

                if (assistantMessageId) {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === assistantMessageId
                        ? { ...msg, charts: [...currentCharts] }
                        : msg
                    )
                  );
                }
              }
              break;

            case 'end':
              if (assistantMessageId) {
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessageId
                      ? { ...msg, isStreaming: false }
                      : msg
                  )
                );
              }
              if (typeof message.content === 'object' && message.content !== null && 'sessionId' in message.content) {
                setSession(message.content as Session);
              }
              setIsLoading(false);
              break;

            case 'error':
              setError(typeof message.content === 'string' ? message.content : 'Unknown error');
              setIsLoading(false);
              break;
          }
        },
        (err) => {
          setError(err.message);
          setIsLoading(false);
        },
        () => {
          setIsLoading(false);
        }
      );

      abortControllerRef.current = () => eventSource.close();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setIsLoading(false);
    }
  }, []);

  const cancelSearch = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
  }, []);

  const loadDemoReport = useCallback((demoId: string) => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current();
      abortControllerRef.current = null;
    }

    const demo = demoReports.find((r) => r.id === demoId);
    if (!demo) {
      console.error(`Demo report not found: ${demoId}`);
      return;
    }

    // Clear previous state and load demo data
    setError(null);
    setIsLoading(false);
    setMessages(demo.messages);
    setSession({
      sessionId: `demo-${demo.id}`,
      status: 'Completed',
      title: demo.title,
      summary: demo.summary,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  }, []);

  const clearChat = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current();
    }
    setMessages([]);
    setSession(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    messages,
    isLoading,
    session,
    error,
    submitQuestion,
    loadDemoReport,
    cancelSearch,
    clearChat,
  };
}
